import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useOrgDateFormat } from '@/lib/timezone';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { type ShareLinkDto, useRevokeShareLink, useShareLinks } from '../api/patient-shares-api';
import { ShareDialog } from './ShareDialog';

interface Props {
  patientId: string;
}

export function ShareLinksTab({ patientId }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [showQrFor, setShowQrFor] = useState<ShareLinkDto | null>(null);
  const { data, isLoading } = useShareLinks(patientId, { status: showAll ? 'all' : 'active' });
  const revoke = useRevokeShareLink(patientId);
  const { formatInstant } = useOrgDateFormat();

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Share links</h3>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={showAll} onCheckedChange={setShowAll} /> Show revoked/expired
        </label>
      </div>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {showAll ? 'No share links yet.' : 'No active share links.'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Views</TableHead>
              <TableHead>Last viewed</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{formatInstant(r.created_at, 'MMM d, HH:mm')}</TableCell>
                <TableCell>{summarizeScope(r.scope)}</TableCell>
                <TableCell>{statusBadge(r)}</TableCell>
                <TableCell className="text-right">{r.view_count}</TableCell>
                <TableCell>
                  {r.last_viewed_at ? formatDistanceToNow(new Date(r.last_viewed_at), { addSuffix: true }) : '—'}
                </TableCell>
                <TableCell>
                  {r.is_expired ? '—' : formatDistanceToNow(new Date(r.expires_at), { addSuffix: true })}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setShowQrFor(r)}>
                      Show QR
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(r.share_url)}>
                      Copy URL
                    </Button>
                    {r.is_active && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => revoke.mutate(r.id)}
                        disabled={revoke.isPending}>
                        Revoke
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ShareDialog
        patientId={patientId}
        open={!!showQrFor}
        onOpenChange={(open) => {
          if (!open) setShowQrFor(null);
        }}
        existingLink={showQrFor}
      />
    </div>
  );
}

function summarizeScope(scope: ShareLinkDto['scope']): string {
  const parts: string[] = [];
  if (scope.demographics) parts.push('Demog');
  if (scope.encounters) parts.push('Enc');
  if (scope.generated_documents) parts.push('Docs');
  if (scope.uploaded_documents) parts.push('Uploads');
  if (scope.pregnancies) parts.push('Preg');
  if (scope.flowsheet) parts.push('Flowsheet');
  return parts.join(', ');
}

function statusBadge(r: ShareLinkDto) {
  if (r.is_revoked) return <Badge variant="destructive">Revoked</Badge>;
  if (r.is_expired) return <Badge variant="secondary">Expired</Badge>;
  return (
    <Badge variant="default" className="bg-success">
      Active
    </Badge>
  );
}
